import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import { Button, Input, Modal } from "../ui";

type EmailRequiredModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  onSubmitEmail: (email: string) => Promise<void> | void;
};

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email is required."),
});

export default function EmailRequiredModal({
  isOpen,
  onClose,
  initialEmail = "",
  onSubmitEmail,
}: EmailRequiredModalProps) {
  const formik = useFormik<{ email: string }>({
    initialValues: { email: initialEmail },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      const nextEmail = values.email.trim();
      try {
        await onSubmitEmail(nextEmail);
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      title="Email required"
      closeIconSrc="/icons/x_circle.svg"
      panelClassName="bg-white p-4"
      headerClassName="rounded-t-[12px] border border-border-grey border-b-0 bg-white px-4 pt-3"
      titleClassName="text-xl !font-medium"
      bodyClassName="rounded-b-[12px] border border-t-0 border-border-grey bg-white px-4 py-4"
    >
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <p className="text-sm text-dark-grey">
          Please add your email to continue payments and billing.
        </p>
        <Input
          id="email-required-input"
          label="Email *"
          type="email"
          size="sm"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name="email"
          placeholder="Email"
          error={
            formik.touched.email
              ? typeof formik.errors.email === "string"
                ? formik.errors.email
                : ""
              : ""
          }
        />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isDisabled={formik.isSubmitting}
          >
            Continue
          </Button>
        </div>
      </form>
    </Modal>
  );
}

